import { CoherencePanel, CoherencePanelSize } from '@coherence-design-system/controls';
import { MessageBar, MessageBarType, PrimaryButton, Spinner, SpinnerSize } from '@fluentui/react';
import * as React from 'react';

import { IProvisioningStatusItem } from '../../../Common/ProvisioningModels/ProvisioningStatus';
import * as PageStyled from '../../../Common/Styles/portalPage.styled';
import * as ViewStyled from '../../../Common/Styles/viewRequest.styled';
import { useProvisioningRetry } from '../../../Hooks/Provisioning/useProvisioningRetry';

import { ProvisioningTimeline } from './ProvisioningTimeline';
import * as Styled from './provisioningDetail.styled';

const DEDUP_WINDOW_MINUTES = 30;

const RETRYABLE_STATUSES = ['Failed', 'PartiallySucceeded'];

function isStaleInProgress(item: IProvisioningStatusItem): boolean {
  if (item.status !== 'InProgress' || !item.startedAt) return false;
  const started = new Date(item.startedAt).getTime();
  const twoHoursMs = 2 * 60 * 60 * 1000;
  return Date.now() - started > twoHoursMs;
}

function isWithinCooldown(item: IProvisioningStatusItem): boolean {
  if (!item.startedAt) return false;
  const started = new Date(item.startedAt).getTime();
  return Date.now() - started < DEDUP_WINDOW_MINUTES * 60 * 1000;
}

interface ProvisioningDetailPanelProps {
  item: IProvisioningStatusItem | null;
  onDismiss: () => void;
}

export function ProvisioningDetailPanel({
  item,
  onDismiss,
}: ProvisioningDetailPanelProps): React.ReactElement {
  const [retry, retryLoading, retryError, retrySuccess, resetRetry] = useProvisioningRetry();

  const canRetry = item != null && (RETRYABLE_STATUSES.includes(item.status) || isStaleInProgress(item));
  const cooldownActive = item != null && canRetry && isWithinCooldown(item);

  const handleDismiss = () => {
    resetRetry();
    onDismiss();
  };

  const onRenderTitleText = () => {
    return <PageStyled.PortalPagePanelText>Provisioning Details</PageStyled.PortalPagePanelText>;
  };

  return (
    <CoherencePanel
      isOpen={item != null}
      onDismiss={handleDismiss}
      onRenderHeader={onRenderTitleText}
      panelSize={CoherencePanelSize.medium}
      closeButtonAriaLabel="Close"
    >
      {item != null && (
        <ViewStyled.ViewRequestContainer>
          <ViewStyled.BasicDetailsContainer>
            <ViewStyled.SubSectionalHeading>
              <ViewStyled.SubSectionalHeadingText>Record Details</ViewStyled.SubSectionalHeadingText>
              <ViewStyled.LineDivider />
            </ViewStyled.SubSectionalHeading>

            <ViewStyled.BasicDetailsSection>
              <ViewStyled.BasicDetailsField>Subject Type</ViewStyled.BasicDetailsField>
              <ViewStyled.BasicDetailsValue>{item.subjectType}</ViewStyled.BasicDetailsValue>
            </ViewStyled.BasicDetailsSection>
            <ViewStyled.BasicDetailsSection>
              <ViewStyled.BasicDetailsField>Subject ID</ViewStyled.BasicDetailsField>
              <ViewStyled.BasicDetailsValue>{item.subjectId}</ViewStyled.BasicDetailsValue>
            </ViewStyled.BasicDetailsSection>
            <ViewStyled.BasicDetailsSection>
              <ViewStyled.BasicDetailsField>Environment</ViewStyled.BasicDetailsField>
              <ViewStyled.BasicDetailsValue>{item.environment}</ViewStyled.BasicDetailsValue>
            </ViewStyled.BasicDetailsSection>
            <ViewStyled.BasicDetailsSection>
              <ViewStyled.BasicDetailsField>Correlation ID</ViewStyled.BasicDetailsField>
              <ViewStyled.BasicDetailsValue>{item.correlationId}</ViewStyled.BasicDetailsValue>
            </ViewStyled.BasicDetailsSection>
            <ViewStyled.BasicDetailsSection>
              <ViewStyled.BasicDetailsField>Action</ViewStyled.BasicDetailsField>
              <ViewStyled.BasicDetailsValue>{item.action}</ViewStyled.BasicDetailsValue>
            </ViewStyled.BasicDetailsSection>
            <ViewStyled.BasicDetailsSection>
              <ViewStyled.BasicDetailsField>Egress Channel</ViewStyled.BasicDetailsField>
              <ViewStyled.BasicDetailsValue>{item.egressChannel || '—'}</ViewStyled.BasicDetailsValue>
            </ViewStyled.BasicDetailsSection>
            <ViewStyled.BasicDetailsSection>
              <ViewStyled.BasicDetailsField>Request Source</ViewStyled.BasicDetailsField>
              <ViewStyled.BasicDetailsValue>{item.requestSource || '—'}</ViewStyled.BasicDetailsValue>
            </ViewStyled.BasicDetailsSection>
            <ViewStyled.BasicDetailsSection>
              <ViewStyled.BasicDetailsField>Request Reason</ViewStyled.BasicDetailsField>
              <ViewStyled.BasicDetailsValue>{item.requestReason || '—'}</ViewStyled.BasicDetailsValue>
            </ViewStyled.BasicDetailsSection>
            <ViewStyled.BasicDetailsSection>
              <ViewStyled.BasicDetailsField>Entity Types</ViewStyled.BasicDetailsField>
              <ViewStyled.BasicDetailsValue>{item.entityTypeNames || item.entityTypeIds || '—'}</ViewStyled.BasicDetailsValue>
            </ViewStyled.BasicDetailsSection>
            <ViewStyled.BasicDetailsSection>
              <ViewStyled.BasicDetailsField>Status</ViewStyled.BasicDetailsField>
              <ViewStyled.BasicDetailsValue>
                <span style={{
                  backgroundColor: statusPillBg(item.status),
                  color: '#ffffff',
                  borderRadius: 4,
                  padding: '1px 7px',
                  fontSize: 11,
                  fontWeight: 600,
                  lineHeight: '18px',
                  whiteSpace: 'nowrap',
                }}>
                  {item.status}
                </span>
              </ViewStyled.BasicDetailsValue>
            </ViewStyled.BasicDetailsSection>
            <ViewStyled.BasicDetailsSection>
              <ViewStyled.BasicDetailsField>Started At</ViewStyled.BasicDetailsField>
              <ViewStyled.BasicDetailsValue>
                {item.startedAt ? new Date(item.startedAt).toLocaleString() : '—'}
              </ViewStyled.BasicDetailsValue>
            </ViewStyled.BasicDetailsSection>
            <ViewStyled.BasicDetailsSection>
              <ViewStyled.BasicDetailsField>Completed At</ViewStyled.BasicDetailsField>
              <ViewStyled.BasicDetailsValue>
                {item.completedAt ? new Date(item.completedAt).toLocaleString() : '—'}
              </ViewStyled.BasicDetailsValue>
            </ViewStyled.BasicDetailsSection>
          </ViewStyled.BasicDetailsContainer>

          {item.errorMessage && (
            <Styled.ErrorMessageField>{item.errorMessage}</Styled.ErrorMessageField>
          )}

          {canRetry && (
            <div style={{ margin: '12px 0' }}>
              {retrySuccess && (
                <MessageBar messageBarType={MessageBarType.success} isMultiline={false} style={{ marginBottom: 8 }}>
                  {retrySuccess}
                </MessageBar>
              )}
              {retryError && (
                <MessageBar messageBarType={MessageBarType.error} isMultiline={false} style={{ marginBottom: 8 }}>
                  {retryError}
                </MessageBar>
              )}
              <PrimaryButton
                text="Retry Provisioning"
                title={cooldownActive ? `Retry available ${DEDUP_WINDOW_MINUTES} minutes after the request was started` : undefined}
                iconProps={{ iconName: 'Refresh' }}
                onClick={() => retry(item)}
                disabled={retryLoading || cooldownActive}
              >
                {retryLoading && <Spinner size={SpinnerSize.xSmall} style={{ marginLeft: 8 }} />}
              </PrimaryButton>
            </div>
          )}

          <ViewStyled.BasicDetailsContainer>
            <ViewStyled.SubSectionalHeading>
              <ViewStyled.SubSectionalHeadingText>Provisioning Steps</ViewStyled.SubSectionalHeadingText>
              <ViewStyled.LineDivider />
            </ViewStyled.SubSectionalHeading>
            {item.steps && item.steps.length > 0 ? (
              <ProvisioningTimeline steps={item.steps} />
            ) : (
              <div style={{ color: '#616161', fontSize: 14 }}>No steps recorded.</div>
            )}
          </ViewStyled.BasicDetailsContainer>
        </ViewStyled.ViewRequestContainer>
      )}
    </CoherencePanel>
  );
}

function statusPillBg(status: string): string {
  switch (status) {
    case 'Succeeded': return '#107c10';
    case 'Failed': return '#d13438';
    case 'PartiallySucceeded': return '#c19c00';
    case 'InProgress':
    case 'PipelineRunning': return '#0078d4';
    case 'Queued': return '#6e7781';
    default: return '#6e7781';
  }
}
